<template>
	<div class="flex flex-col items-center justify-center pt-2">
		<SheetHeader>
			<SheetTitle>{{ readonly ? 'Node config:' : 'Edit node:' }} {{ node.name }}</SheetTitle>
			<SheetDescription>
				<!-- Make changes to your {{ obj.name }} node here. Click save when you're done. -->
			</SheetDescription>
		</SheetHeader>
		<div class="w-full">
			<pre><code>{{  JSON.stringify(varsMenu, null, 2) }}</code></pre>
		</div>
		<div class="w-full">
			<div class="mb-6 mt-2 w-full border-b border-dark dark:border-light">
				<h2 class="mb-2">ID</h2>
			</div>
			<div class="flex w-full flex-wrap gap-2">
				<div key="node_id" class="mb-2 w-full flex-grow px-2 md:mb-5">
					<div class="formGroup flex flex-col">
						<label class="textGrey truncate" for="node_id">Your ref (must begin with a-z, no spaces)</label>
						<FormKit
							id="node_id"
							v-model="settings.node_id"
							type="text"
							class="form-control"
							:disabled="readonly"
							:required="true"
							name="node_id"
							validation="matches:/^[a-zA-Z][a-zA-Z0-9_\-]*$/"
							validation-visibility="dirty"
							@dragover="(e) => ((e.dataTransfer.dropEffect = 'none'), e.preventDefault())"
						/>
					</div>
				</div>
			</div>
		</div>
		<div v-for="(inputs, group) in filteredInputsByGroup" :key="group" class="w-full">
			<div class="mb-6 mt-2 w-full border-b border-dark dark:border-light">
				<h2 class="mb-2">{{ group }}</h2>
			</div>
			<div class="flex w-full flex-wrap gap-2">
				<div
					v-for="input in inputs"
					:key="input.name"
					class="px-2"
					:class="input.fullWidth ? 'w-full' : 'w-full md:w-[calc(50%-0.25rem)]'"
					@dragover="(e) => !input.enableVars && ((e.dataTransfer.dropEffect = 'none'), e.preventDefault())"
				>
					<div
						class="formGroup mb-2 flex flex-col md:mb-5"
						:class="[
							input.type === 'checkbox' ? 'flex-row items-center gap-2' : '',
							['select', 'nodes', 'siptrunk', 'mobilesim', 'file'].includes(input.type) ? 'withIcon' : ''
						]"
					>
						<label class="textGrey truncate" :for="input.name">{{ input.label }}</label>
						<FormKit
							v-if="
								[
									'text',
									'email',
									'tel',
									'url',
									'number',
									'textarea',
									'color',
									'date',
									'time',
									'range',
									'radio',
									'password'
								].includes(input.type)
							"
							:id="input.name"
							v-model="settings[input.name]"
							:type="input.type"
							:disabled="readonly"
							class="form-control"
							:placeholder="input.placeholder || ''"
							:name="input.label"
							:validation="getValidation(input)"
							:validation-messages="{ matches: input.regErr || '' }"
							:options="input.options"
							:accept="input.fileType"
							validation-visibility="dirty"
							:suffix-icon="input.type === 'password' ? 'eyeClosed' : null"
							:suffix-icon-class="
								input.type === 'password' ? 'hover:text-main dark:hover:text-offwhite' : ''
							"
							@suffix-icon-click="input.type === 'password' ? handleIconClick : null"
						/>
						<select v-else-if="input.type === 'select'" v-model="settings[input.name]">
							<option v-for="option in input.options" :key="option.value" :value="option.value">
								{{ option.label }}
							</option>
						</select>
						<select v-else-if="input.type === 'nodes'" v-model="settings[input.name]">
							<option v-for="nodeName in allNodes()" :key="nodeName" :value="nodeName">
								{{ nodeName }}
							</option>
						</select>
						<input
							v-else-if="input.type === 'checkbox'"
							:id="input.name"
							v-model="settings[input.name]"
							type="checkbox"
							class="tw-toggle"
							:disabled="readonly"
							:name="input.label"
						/>
						<div v-else-if="input.type === 'siptrunk'" class="flex flex-col gap-1">
							<div class="flex items-center gap-2">
								<select v-model="settings[input.name]" :disabled="readonly">
									<option :value="undefined">-- Select a SIP Trunk --</option>
									<option v-for="trunk in siptrunks" :key="trunk.id" :value="trunk.id">
										{{ trunk.username }}
									</option>
								</select>
							</div>
						</div>
						<div v-else-if="input.type === 'mobilesim'" class="flex flex-col gap-1">
							<div class="flex items-center gap-2">
								<select v-model="settings[input.name]" :disabled="readonly">
									<option :value="undefined">-- Select a Mobile SIM --</option>
									<option v-for="sim in mobileSims" :key="sim.id" :value="sim.id">
										{{ sim.number }}{{ sim.description ? ' - ' + sim.description : '' }}
									</option>
								</select>
							</div>
						</div>
						<div v-else-if="input.type === 'file'" class="flex flex-col gap-1">
							<div class="flex items-center gap-2">
								<div class="formGroup withIcon relative mr-2 w-full">
									<select v-model="settings[input.name]" :disabled="readonly">
										<option :value="undefined">-- Select a file --</option>
										<option
											v-for="opt in input.additionalOptions"
											:key="opt.value"
											:value="opt.value"
										>
											{{ opt.label }}
										</option>
										<option
											v-for="file in files"
											:key="file.id"
											:value="file.id + '.' + file.fileExt"
										>
											{{ file.name }}
										</option>
									</select>
									<span class="fa-light fa-chevron-down"></span>
								</div>
								<div v-if="!readonly" class="flex w-4 items-center justify-end gap-2 sm:my-0">
									<button class="w-6" @click="isUploadOpen = true">
										<span class="fa-light fa-upload"></span>
									</button>
								</div>
								<div v-if="!readonly" class="flex w-4 items-center justify-end gap-2 sm:my-0">
									<button class="w-6" @click="isRecordOpen = true">
										<span class="fa-light fa-microphone"></span>
									</button>
								</div>
								<upload-file-audio
									:selected-tenant-id="selectedTenantId"
									:selected-account-id="selectedAccountId"
									:disable-overlay-close="true"
									:open="isUploadOpen"
									@close="(created) => onClose(input.name, created)"
								/>
								<upload-record-audio
									v-if="isRecordOpen"
									:selected-tenant-id="selectedTenantId"
									:selected-account-id="selectedAccountId"
									:disable-overlay-close="true"
									:open="true"
									@close="(created) => onClose(input.name, created)"
								/>
							</div>
						</div>
						<div v-else-if="input.type === 'multi'" class="flex flex-col gap-1">
							<div
								v-for="(option, index) in settings[input.name]"
								:key="index"
								class="flex w-full items-center gap-2"
							>
								<FormKit
									:id="`${input.name}-${index}`"
									v-model="settings[input.name][index]"
									:type="input.subType"
									class="form-control w-full"
									:placeholder="input.placeholder || ''"
									:name="`${input.label}-${index}`"
									:validation="getValidation(input)"
									:validation-messages="{ matches: input.regErr || '' }"
									:options="input.options"
									:accept="input.fileType"
									validation-visibility="dirty"
								/>
								<button class="jmiBtn red" @click="removeOption(input.name, index)">
									<span class="fa-light fa-remove"></span>
								</button>
							</div>
							<div
								v-if="!settings[input.name] || settings[input.name]?.length === 0"
								class="flex items-center gap-2"
							>
								<p class="textGrey">No options added</p>
							</div>
							<button
								class="jmiBtn green mt-2"
								:disabled="
									settings[input.name]?.length &&
									!settings[input.name][settings[input.name].length - 1]
								"
								@click="addOption(input.name)"
							>
								Add
							</button>
						</div>
						<div v-else-if="input.type === 'multiKeyValue'" class="flex flex-col gap-1">
							<div
								v-for="(option, index) in settings[input.name]"
								:key="index"
								class="flex w-full items-center gap-2"
							>
								<div class="form-control" :class="input.width1 ? input.width1 : 'w-full'">
									<FormKit
										:id="`${input.name}-${index}`"
										v-model="settings[input.name][index].key"
										:type="input.type1 || 'text'"
										:placeholder="input.placeholder1 || input.keyPlaceholder || ''"
										:name="`${input.label}-${index}`"
										:validation="getValidation(input)"
										:validation-messages="{ matches: input.regErr || '' }"
										:options="input.options"
										:accept="input.fileType"
										validation-visibility="dirty"
									/>
								</div>
								<div class="form-control" :class="input.width2 ? input.width2 : 'w-full'">
									<FormKit
										:id="`${input.name}-key-${index}`"
										v-model="settings[input.name][index].value"
										:type="input.type2 || 'text'"
										:placeholder="input.placeholder2 || input.placeholder || ''"
										:name="`${input.label}-key-${index}`"
										:validation="getValidation(input)"
										:validation-messages="{ matches: input.regErr || '' }"
										:options="input.options"
										:accept="input.fileType"
										validation-visibility="dirty"
									/>
								</div>
								<button class="jmiBtn red" @click="removeKey(input.name, index)">
									<span class="fa-light fa-remove"></span>
								</button>
							</div>
							<div
								v-if="!settings[input.name] || settings[input.name]?.length === 0"
								class="flex items-center gap-2"
							>
								<p class="textGrey">{{ input.emptyText || 'No options added' }}</p>
							</div>
							<button
								class="jmiBtn green mt-2"
								:disabled="
									settings[input.name]?.length &&
									!settings[input.name][settings[input.name].length - 1].key
								"
								@click="addKey(input.name)"
							>
								Add
							</button>
						</div>
						<div v-else-if="input.type === 'flow'" class="flex flex-col gap-1">
							<div class="flex items-center gap-2">
								<select v-model="settings[input.name]">
									<option :value="undefined">-- Select a flow --</option>
									<option v-for="flow in flows" :key="flow.id" :value="flow.id">
										{{ flow.name }}
									</option>
								</select>
							</div>
						</div>
						<div v-else-if="input.type.indexOf('code-') === 0" class="flex flex-col gap-1">
							<div class="flex items-center gap-2">
								<v-ace-editor
									v-model:value="settings[input.name]"
									:lang="input.type.replace('code-', '')"
									:theme="isDarkMode ? 'github_dark' : 'chrome'"
									:min-lines="input.minLines || 15"
									:max-lines="input.maxLines || 30"
									class="w-full"
								/>
							</div>
						</div>
						<div v-else-if="input.type === 'timeconditions'" class="flex flex-col gap-1">
							<TimeConditionView
								:saved-data="settings[input.name]"
								@update="(data) => (settings[input.name] = data)"
							/>
						</div>
						<div v-else-if="input.type === 'tts_voices'" class="flex flex-col gap-1">
							<TTSVoiceList
								:saved-data="settings[input.name]"
								@update="(voice) => (settings[input.name] = voice)"
							/>
						</div>
						<span
							v-if="['select', 'nodes', 'siptrunk', 'mobilesim'].includes(input.type)"
							class="fa-light fa-chevron-down"
						></span>
					</div>
				</div>
			</div>
		</div>
		<SheetFooter class="flex flex-row gap-2">
			<SheetClose v-if="!readonly" as-child>
				<button class="jmiBtn green" type="submit" @click="$emit('save', settings)">Save changes</button>
			</SheetClose>
			<SheetClose as-child>
				<button class="jmiBtn red" @click.stop="$emit('close')">
					{{ readonly ? 'Close' : 'Cancel' }}
				</button>
			</SheetClose>
		</SheetFooter>
	</div>
</template>

<script>
import { SheetClose, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/nodesheet'
import { VAceEditor } from 'vue3-ace-editor'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/mode-html'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/theme-chrome'
import 'ace-builds/src-noconflict/theme-github_dark'
import TimeConditionView from '@/components/flow/TimeConditionView'
import TTSVoiceList from '@/components/flow/TTSVoiceList'
import filesApi from '@/scripts/files/files.api'
import siptrunksApi from '@/scripts/siptrunks/siptrunks.api'
import mobileSimsApi from '@/scripts/mobilesims/mobilesims.api'

export default {
	components: {
		SheetClose,
		SheetDescription,
		SheetFooter,
		SheetHeader,
		SheetTitle,
		VAceEditor,
		TimeConditionView,
		TTSVoiceList
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: [
		'node',
		'nodeData',
		'closeHandler',
		'selectedTenantId',
		'selectedAccountId',
		'readonly',
		'flows',
		'varsMenu',
		'allNodes'
	],
	data() {
		return {
			settings: {},
			inputsByGroup: {},
			isOpen: true,
			files: [],
			siptrunks: [],
			mobileSims: [],
			isUploadOpen: false,
			isRecordOpen: false,
			selectedNodeId: null,
			selectedNodeVar: null
		}
	},
	computed: {
		isDarkMode() {
			return document.documentElement.classList.contains('dark')
		},
		filteredInputsByGroup() {
			return Object.keys(this.inputsByGroup).reduce((acc, key) => {
				if (this.inputsByGroup[key].length) {
					acc[key] = this.inputsByGroup[key].filter((input) => this.checkDependsOn(input, this.settings))
				}
				return acc
			}, {})
		}
	},
	mounted() {
		this.settings = this.nodeData ? { ...this.nodeData } : {}

		let hasFileInputs = false
		let hasSipTrunkInputs = false
		let hasMobileSimsInputs = false

		this.node.properties?.forEach((p) => {
			if (p.type === 'file') hasFileInputs = true
			if (p.type === 'siptrunk') hasSipTrunkInputs = true
			if (p.type === 'mobilesim') hasMobileSimsInputs = true

			if (!this.inputsByGroup[p.group]) {
				this.inputsByGroup[p.group] = []
			}
			this.inputsByGroup[p.group].push(p)
		})

		if (hasFileInputs) {
			filesApi
				.getAll({
					where: {
						selectedTenant: this.selectedTenantId,
						selectedAccount: this.selectedAccountId
					}
				})
				.then((res) => {
					this.files = res.data.results
				})
		}

		if (hasSipTrunkInputs) {
			siptrunksApi
				.getAll({
					where: {
						selectedTenant: this.selectedTenantId,
						selectedAccount: this.selectedAccountId
					}
				})
				.then((res) => {
					this.siptrunks = res.data.results.sort((a, b) => a.username.localeCompare(b.username))
				})
		}

		if (hasMobileSimsInputs) {
			mobileSimsApi
				.getAll({
					where: {
						selectedTenant: this.selectedTenantId,
						selectedAccount: this.selectedAccountId
					}
				})
				.then((res) => {
					this.mobileSims = res.data.results.sort((a, b) => a.number.localeCompare(b.number))
				})
		}

		this.closeHandler(() => {
			if (JSON.stringify(this.settings) !== JSON.stringify(this.nodeData))
				return {
					orignal: this.nodeData,
					new: this.settings,
					originalString: JSON.stringify(this.nodeData),
					newString: JSON.stringify(this.settings)
				}
			return null
		})
	},
	methods: {
		handleIconClick(node) {
			node.props.suffixIcon = node.props.suffixIcon === 'eye' ? 'eyeClosed' : 'eye'
			node.props.type = node.props.type === 'password' ? 'text' : 'password'
		},
		getValidation(input) {
			if (input.required && input.regex) {
				return `required|matches:${input.regex}`
			} else if (input.required) {
				return 'required'
			} else if (input.regex) {
				return `matches:${input.regex}`
			}
			return ''
		},
		onClose(inputName, created) {
			this.isUploadOpen = false
			this.isRecordOpen = false

			console.log(created)

			if (created?.id) {
				this.files = [...this.files, created]
				this.settings[inputName] = created.id + '.' + created.fileExt
			}
		},
		addOption(inputName) {
			if (!this.settings[inputName]) {
				this.settings = { ...this.settings, [inputName]: [''] }
			} else {
				this.settings[inputName] = [...this.settings[inputName], '']
			}
		},
		removeOption(inputName, index) {
			this.settings[inputName].splice(index, 1)
		},
		addKey(inputName) {
			if (!this.settings[inputName]) {
				this.settings = { ...this.settings, [inputName]: [{ key: '', value: '' }] }
			} else {
				this.settings[inputName].push({ key: '', value: '' })
			}
		},
		removeKey(inputName, index) {
			this.settings[inputName].splice(index, 1)
		},
		checkDependsOn(input, settings) {
			if (input.dependsOn?.indexOf('===') > -1) {
				const [key, value] = input.dependsOn.split('===')

				return settings[key.trim()] === value.trim()
			}
			if (input.hideOn?.indexOf('===') > -1) {
				const [key, value] = input.hideOn.split('===')

				return settings[key.trim()] !== value.trim()
			}
			return input.dependsOn ? settings[input.dependsOn] : input.hideOn ? !settings[input.hideOn] : true
		}
	}
}
</script>
